// styles/GlobalStyle.ts
const GlobalStyle = {
  // Layout & Containers
  container: "min-h-screen bg-[#f8f9fa]",

  // Google Adsense Specific Colors
  colors: {
    blue: {
      50: "#e8f0fe",
      100: "#d2e3fc",
      200: "#aecbfa",
      300: "#8ab4f8",
      400: "#669df6",
      500: "#4285f4",
      600: "#1a73e8",
      700: "#1967d2",
      800: "#185abc",
      900: "#174ea6",
    },
    green: {
      50: "#e6f4ea",
      100: "#ceead6",
      200: "#a8dab5",
      300: "#81c995",
      400: "#5bb974",
      500: "#34a853",
      600: "#1e8e3e",
      700: "#188038",
      800: "#137333",
      900: "#0d652d",
    },
    yellow: {
      50: "#fef7e0",
      100: "#feefc3",
      200: "#fde293",
      300: "#fdd663",
      400: "#fcc934",
      500: "#fbbc04",
      600: "#f9ab00",
      700: "#f29900",
      800: "#ea8600",
      900: "#e37400",
    },
    red: {
      50: "#fce8e6",
      100: "#fad2cf",
      200: "#f6b4b2",
      300: "#f28b82",
      400: "#ee675c",
      500: "#ea4335",
      600: "#d33426",
      700: "#b31412",
      800: "#9c1b1b",
      900: "#800000",
    },
    gray: {
      50: "#f8f9fa",
      100: "#f1f3f4",
      200: "#e8eaed",
      300: "#dadce0",
      400: "#bdc1c6",
      500: "#9aa0a6",
      600: "#80868b",
      700: "#5f6368",
      800: "#3c4043",
      900: "#202124",
    },
  },

  // Typography
  heading1: "text-2xl font-normal text-[#202124] tracking-tight",
  heading2: "text-lg font-medium text-[#202124]",
  heading3: "text-base font-medium text-[#202124]",
  bodyText: "text-sm text-[#5f6368]",
  bodyDark: "text-sm text-[#202124]",
  caption: "text-xs text-[#5f6368]",

  // Button Styles - FIXED: Added standalone button classes
  buttonPrimary:
    "px-4 py-2 bg-[#1a73e8] text-white rounded-lg hover:bg-[#0d62d9] transition-colors",
  buttonSecondary:
    "px-4 py-2 bg-white border border-[#dadce0] text-[#1a73e8] rounded-lg hover:bg-[#f8f9fa] transition-colors",
  button: {
    base: "px-4 py-2 rounded-lg transition-colors",
    primary: "bg-[#1a73e8] text-white hover:bg-[#0d62d9]",
    secondary:
      "bg-white border border-[#dadce0] text-[#1a73e8] hover:bg-[#f8f9fa] hover:text-[#0d62d9]",
    danger: "bg-[#ea4335] text-white hover:bg-[#d33426]",
    sizes: {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2",
      lg: "px-6 py-3 text-lg",
    },
  },

  // Cards (Google Style)
  card: "bg-white rounded-lg border border-[#dadce0]",
  cardHover: "hover:border-[#1a73e8] hover:shadow-md transition-all",

  // Inputs
  input:
    "w-full px-3 py-2 border border-[#dadce0] rounded hover:border-[#1a73e8] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] outline-none transition",
  label: "block text-sm font-medium text-[#5f6368] mb-1",

  // Tables
  tableHeader: "bg-[#f8f9fa] border-b border-[#dadce0]",
  tableHeaderCell:
    "px-4 py-3 text-xs font-medium text-[#5f6368] uppercase tracking-wider text-left",
  tableRow: "border-b border-[#dadce0] last:border-0 hover:bg-[#f8f9fa]",

  // Navigation
  nav: {
    item: "flex items-center px-3 py-2 rounded-lg text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124] transition-all",
    itemActive:
      "bg-[#e8f0fe] text-[#1a73e8] hover:bg-[#e8f0fe] hover:text-[#1a73e8]",
  },

  // Status Indicators
  status: {
    success: "bg-[#e6f4ea] text-[#0d652d]",
    warning: "bg-[#fef7e0] text-[#e37400]",
    error: "bg-[#fce8e6] text-[#d33426]",
    info: "bg-[#e8f0fe] text-[#1a73e8]",
    badge:
      "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
  },

  // Alert Styles
  alertSuccess:
    "bg-[#e6f4ea] border border-[#81c995] text-[#0d652d] p-4 rounded-lg",
  alertWarning:
    "bg-[#fef7e0] border border-[#fbbc04] text-[#e37400] p-4 rounded-lg",
  alertError:
    "bg-[#fce8e6] border border-[#f28b82] text-[#d33426] p-4 rounded-lg",
  alertInfo:
    "bg-[#e8f0fe] border border-[#aecbfa] text-[#1a73e8] p-4 rounded-lg",

  // Layout Utilities - FIXED: Added grid utility classes
  gridCols2: "grid grid-cols-1 md:grid-cols-2 gap-4",
  gridCols3: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
  gridCols4: "grid grid-cols-2 md:grid-cols-4 gap-4",

  grid: {
    cols2: "grid grid-cols-1 md:grid-cols-2 gap-4",
    cols3: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
    cols4: "grid grid-cols-2 md:grid-cols-4 gap-4",
  },

  // Spacing
  spacing: {
    section: "mb-6",
    content: "p-6",
  },

  // Shadows
  shadow: {
    sm: "shadow-sm",
    md: "shadow-md",
    hover: "hover:shadow-md transition-shadow",
  },

  // Transitions
  transition: "transition-all duration-200 ease-in-out",

  // Z-index
  zIndex: {
    dropdown: "z-50",
    modal: "z-100",
    toast: "z-1000",
  },

  // Chart Styles
  chartContainer: "bg-white rounded-lg border border-[#dadce0] p-6",
  chartTitle: "font-medium text-[#202124] mb-4",

  // Summary Card Styles
  adsenseCard:
    "bg-white rounded-lg border border-[#dadce0] p-6 hover:border-[#1a73e8] hover:shadow-sm transition-all",
  trendUp: "text-[#0d652d]",
  trendDown: "text-[#d33426]",
  metricValue: "text-2xl font-normal text-[#202124] mb-1",
  metricLabel: "text-xs text-[#5f6368] uppercase tracking-wider",
};

export default GlobalStyle;
